import { ComponentProps } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Divider } from '../data-display/divider/divider';
import { List, ListItem } from '../data-display/list/list';
import { Box } from '../layout/box/box';
import { Link } from '../navigation/link/link';
import { Heading } from './heading';
import { Text } from './text';

/**
 * Markdown components for react-markdown that use explicit semantic elements
 * from the design system instead of Box as X pattern.
 *
 * Provides consistent styling for all markdown elements with proper spacing,
 * typography hierarchy, and semantic HTML structure.
 */
const markdownComponents = {
  // Links with external styling
  a: ({ node, ...props }: ComponentProps<'a'> & { node?: unknown }) => (
    <Link
      href={props.href}
      isExternal
      rel="noopener noreferrer"
      fontSize="md"
      {...props}
    />
  ),

  // Paragraphs with proper spacing
  p: ({ node, ...props }: ComponentProps<'p'> & { node?: unknown }) => (
    <Text variant="main" mb="4" lineHeight="1.6" fontSize="md" {...props} />
  ),

  // Headings with proper hierarchy and spacing
  h1: ({ node, ...props }: ComponentProps<'h1'> & { node?: unknown }) => (
    <Heading
      variant="h3"
      mb="4"
      mt="6"
      fontWeight="bold"
      color="gray.900"
      {...props}
    />
  ),
  h2: ({ node, ...props }: ComponentProps<'h2'> & { node?: unknown }) => (
    <Heading
      variant="h4"
      mb="3"
      mt="5"
      fontWeight="semibold"
      color="gray.800"
      {...props}
    />
  ),
  h3: ({ node, ...props }: ComponentProps<'h3'> & { node?: unknown }) => (
    <Heading
      variant="h5"
      mb="3"
      mt="4"
      fontWeight="semibold"
      color="gray.700"
      {...props}
    />
  ),
  h4: ({ node, ...props }: ComponentProps<'h4'> & { node?: unknown }) => (
    <Heading
      variant="h6"
      mb="2"
      mt="4"
      fontWeight="medium"
      color="gray.700"
      {...props}
    />
  ),
  h5: ({ node, ...props }: ComponentProps<'h5'> & { node?: unknown }) => (
    <Text
      fontSize="lg"
      fontWeight="medium"
      mb="2"
      mt="3"
      color="gray.600"
      {...props}
    />
  ),
  h6: ({ node, ...props }: ComponentProps<'h6'> & { node?: unknown }) => (
    <Text
      fontSize="md"
      fontWeight="medium"
      mb="2"
      mt="3"
      color="gray.600"
      {...props}
    />
  ),

  // Lists with proper spacing and nesting
  ul: ({ node, ...props }: ComponentProps<'ul'> & { node?: unknown }) => (
    <List
      listStyleType="disc"
      mb="4"
      pl="6"
      fontSize="md"
      spacing="1"
      {...props}
    />
  ),
  ol: ({ node, ...props }: ComponentProps<'ol'> & { node?: unknown }) => (
    <List
      as="ol"
      listStyleType="decimal"
      mb="4"
      pl="6"
      spacing="1"
      fontSize="md"
      {...props}
    />
  ),
  li: ({ node, ...props }: ComponentProps<'li'> & { node?: unknown }) => (
    <ListItem
      lineHeight="1.6"
      mb="1"
      fontSize="md"
      sx={{
        '& > ul, & > ol': {
          mt: '2',
          mb: '2',
          pl: '4',
        },
        '& > p': {
          mb: '1',
        },
      }}
      {...props}
    />
  ),

  // Code elements
  code: ({ node, ...props }: ComponentProps<'code'> & { node?: unknown }) => (
    <Text
      as="code"
      fontSize="sm"
      fontFamily="mono"
      bg="gray.100"
      px="1"
      py="0.5"
      borderRadius="sm"
      color="gray.800"
      {...props}
    />
  ),
  pre: ({ node, ...props }: ComponentProps<'pre'> & { node?: unknown }) => (
    <Box
      as="pre"
      bg="gray.50"
      p="4"
      borderRadius="md"
      border="1px"
      borderColor="gray.200"
      mb="4"
      overflow="auto"
      fontSize="sm"
      fontFamily="mono"
      {...props}
    />
  ),

  // Blockquotes
  blockquote: ({
    node,
    ...props
  }: ComponentProps<'blockquote'> & { node?: unknown }) => (
    <Box
      as="blockquote"
      borderLeft="4px"
      borderColor="blue.400"
      pl="4"
      py="2"
      bg="blue.50"
      mb="4"
      fontStyle="italic"
      color="gray.700"
      {...props}
    />
  ),

  // Horizontal rule
  hr: ({ node, ...props }: ComponentProps<'hr'> & { node?: unknown }) => (
    <Divider my="6" borderColor="gray.300" {...props} />
  ),

  // Strong and emphasis
  strong: ({
    node,
    ...props
  }: ComponentProps<'strong'> & { node?: unknown }) => (
    <Text as="strong" color="black" fontWeight="semibold" {...props} />
  ),
  em: ({ node, ...props }: ComponentProps<'em'> & { node?: unknown }) => (
    <Text as="em" fontStyle="italic" {...props} />
  ),

  // Table elements
  table: ({ node, ...props }: ComponentProps<'table'> & { node?: unknown }) => (
    <Box
      as="table"
      width="full"
      mb="6"
      border="1px"
      borderColor="gray.300"
      borderRadius="lg"
      overflow="hidden"
      boxShadow="sm"
      bg="white"
      sx={{
        borderCollapse: 'collapse',
      }}
      {...props}
    />
  ),
  thead: ({ node, ...props }: ComponentProps<'thead'> & { node?: unknown }) => (
    <Box as="thead" bg="gray.50" {...props} />
  ),
  tbody: ({ node, ...props }: ComponentProps<'tbody'> & { node?: unknown }) => (
    <Box as="tbody" bg="white" {...props} />
  ),
  tr: ({ node, ...props }: ComponentProps<'tr'> & { node?: unknown }) => (
    <Box
      as="tr"
      borderBottom="1px"
      borderColor="gray.200"
      _hover={{ bg: 'gray.25' }}
      transition="background-color 0.15s ease"
      _last={{ borderBottom: 'none' }}
      {...props}
    />
  ),
  th: ({ node, ...props }: ComponentProps<'th'> & { node?: unknown }) => (
    <Box
      as="th"
      px="4"
      py="3"
      textAlign="left"
      fontWeight="semibold"
      fontSize="sm"
      color="gray.700"
      bg="gray.50"
      borderBottom="2px"
      borderColor="gray.300"
      _first={{ borderTopLeftRadius: 'lg' }}
      _last={{ borderTopRightRadius: 'lg' }}
      {...props}
    />
  ),
  td: ({ node, ...props }: ComponentProps<'td'> & { node?: unknown }) => (
    <Box
      as="td"
      px="4"
      py="3"
      fontSize="sm"
      color="gray.600"
      lineHeight="1.5"
      verticalAlign="top"
      {...props}
    />
  ),

  // Image
  img: ({ node, ...props }: ComponentProps<'img'> & { node?: unknown }) => (
    <Box as="img" maxW="full" h="auto" mb="4" borderRadius="md" {...props} />
  ),
};

export type MarkdownProps = {
  /**
   * The markdown content to render
   */
  children: string;
  /**
   * Custom components to override default markdown components
   */
  components?: ComponentProps<typeof ReactMarkdown>['components'];
} & Omit<ComponentProps<typeof ReactMarkdown>, 'components' | 'children'>;

/**
 * Markdown component that renders markdown content using our design system components.
 *
 * Uses explicit semantic elements (Heading, Text, List, etc.) instead of Box as X pattern
 * for better accessibility and consistency with the design system.
 *
 * @example
 * ```tsx
 * <Markdown>
 *   # Hello World
 *   This is **bold** and *italic* text.
 * </Markdown>
 * ```
 */
export const Markdown = ({
  children,
  components,
  ...props
}: MarkdownProps): JSX.Element => {
  const mergedComponents = components
    ? { ...markdownComponents, ...components }
    : markdownComponents;

  return (
    <ReactMarkdown
      components={mergedComponents}
      remarkPlugins={[remarkGfm]}
      {...props}
    >
      {children}
    </ReactMarkdown>
  );
};
